import type { PageContextEnvelope } from 'src/components/PageContext'

import { trimToMax } from './utils'

const MAX_ROUTE_TEXT_CHARS = 200

type ComposePageContextTextArgs = {
  routePath: string
  viewer: 'citizen' | 'staff'
  sanitizedHtml: string
  structuredPageContextText?: string
  structuredPageContextEnvelope?: PageContextEnvelope
}

const getCombinedMetadataLines = (args: {
  routePath: string
  viewer: 'citizen' | 'staff'
  hasStructuredPageContextText: boolean
  structuredPageContextEnvelope?: PageContextEnvelope
}): string[] => {
  const { routePath, viewer, hasStructuredPageContextText, structuredPageContextEnvelope } = args

  const entityIds = structuredPageContextEnvelope?.page.entityIds
  const metadataLines = [
    `route: ${trimToMax(routePath, MAX_ROUTE_TEXT_CHARS)}`,
    `viewer: ${viewer}`,
  ]

  // Keep page_meta minimal when structured context is present to avoid redundant facts.
  if (!hasStructuredPageContextText) {
    if (structuredPageContextEnvelope?.page.pageType) {
      metadataLines.push(`pageType: ${structuredPageContextEnvelope.page.pageType}`)
    }
    if (entityIds?.recordUuid) {
      metadataLines.push(`recordUuid: ${entityIds.recordUuid}`)
    }
    if (entityIds?.recordId) {
      metadataLines.push(`recordNumber: ${entityIds.recordId}`)
    }
    if (entityIds?.templateUuid) {
      metadataLines.push(`templateUuid: ${entityIds.templateUuid}`)
    }
    if (entityIds?.templateId) {
      metadataLines.push(`templateId: ${entityIds.templateId}`)
    }
    if (entityIds?.orgSlug) {
      metadataLines.push(`orgSlug: ${entityIds.orgSlug}`)
    }
  }

  return metadataLines
}

export const composePageContextText = (args: ComposePageContextTextArgs): string => {
  const {
    routePath,
    viewer,
    sanitizedHtml,
    structuredPageContextText,
    structuredPageContextEnvelope,
  } = args

  const hasStructuredPageContextText = !!structuredPageContextText?.trim()
  const metadataLines = getCombinedMetadataLines({
    routePath,
    viewer,
    hasStructuredPageContextText,
    structuredPageContextEnvelope,
  })

  const payloadParts = [
    ...(hasStructuredPageContextText
      ? [`<structured_page_context>\n${structuredPageContextText}\n</structured_page_context>`]
      : []),
    `<page_meta>\n${metadataLines.join('\n')}\n</page_meta>`,
    `<sanitized_html>\n${sanitizedHtml}\n</sanitized_html>`,
  ]

  return payloadParts.join('\n')
}
